import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useLabOrder } from '../labs-order.page';
import { ROUTE_LABS, ROUTE_LABS_ORDER_STEP_1, ROUTE_LABS_ORDER_STEP_4 } from '../../../../constants/routes';
import { LabsOrderStepLayout } from '../components';
import { OrderStatus } from '../../../../api/generated-catch-backend/model';
import {
  usePatchLabOrderV1LabsOrdersOrderIdPatch,
  useListLabOrdersV1LabsOrdersGet,
} from '../../../../api/generated-catch-backend/laboratory-orders/laboratory-orders';
import { useDownloadLabOrder } from '../hooks/useDownloadLabOrder';
import { useLabOrderPolling } from '../hooks/useLabOrderPolling';

export function Step5Success() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { activeOrder, isLoadingOrder, refetchOrder } = useLabOrder();
  const patchOrderMutation = usePatchLabOrderV1LabsOrdersOrderIdPatch();
  const { refetch: refetchOrdersList } = useListLabOrdersV1LabsOrdersGet({
    query: {
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
    },
  });
  const [isCancelling, setIsCancelling] = useState(false);
  const { downloadOrder, isDownloading } = useDownloadLabOrder();

  // Poll for order status when pending signature
  const { isPolling, hasTimedOut } = useLabOrderPolling({
    orderStatus: activeOrder?.status,
    refetchOrder,
  });

  // Refetch order data when component mounts to ensure we have latest requisition status
  useEffect(() => {
    if (activeOrder?.orderId) {
      refetchOrder();
    }
  }, [activeOrder?.orderId, refetchOrder]);

  // Check if we arrived here due to an error
  const hasError = searchParams.get('error') === 'true';

  // Also check if order is in a state that indicates confirmation never succeeded
  // If we're on step 5 but order is still in psc_selected or earlier, confirmation must have failed
  const orderInFailedState =
    activeOrder?.status === OrderStatus.psc_selected || activeOrder?.status === OrderStatus.patient_details_collected;

  const handleBack = () => {
    navigate(ROUTE_LABS_ORDER_STEP_4);
  };

  const handleClose = () => {
    navigate(ROUTE_LABS);
  };

  const handleContactSupport = () => {
    window.Intercom?.('show');
  };

  const handleTryAgain = async () => {
    // If there's an active order, cancel it first before starting fresh
    if (activeOrder) {
      setIsCancelling(true);
      try {
        await patchOrderMutation.mutateAsync({
          orderId: activeOrder.orderId,
          data: {
            operation: 'cancel_order',
            data: {
              cancellationReason: 'User restarting order flow after error',
            },
          },
        });

        // Refetch both the single order AND the orders list
        // This ensures the parent context picks up the cancelled status
        await Promise.all([refetchOrder(), refetchOrdersList()]);
      } catch (error) {
        console.error('Error cancelling order:', error);
        // Continue anyway - worst case user will see conflict error in step 2
      } finally {
        setIsCancelling(false);
      }
    }

    // Navigate to step 1 to start fresh
    navigate(ROUTE_LABS_ORDER_STEP_1);
  };

  if (isLoadingOrder) {
    return (
      <LabsOrderStepLayout currentStep={5} onBack={handleBack} onClose={handleClose}>
        <div className="py-16 text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-[#7CF1C7]"></div>
          <p className="text-[#3B3B3B]">Loading order details...</p>
        </div>
      </LabsOrderStepLayout>
    );
  }

  // Show error state if:
  // 1. Explicitly flagged with ?error=true query param
  // 2. Order is missing entirely
  // 3. Order exists but is in a pre-confirmation state (confirmation must have failed)
  if (hasError || !activeOrder || orderInFailedState) {
    return (
      <LabsOrderStepLayout currentStep={5} onBack={handleBack} onClose={handleClose}>
        <div className="flex flex-col items-center justify-center px-4 py-16">
          <h1 className="mb-4 text-center text-[32px] leading-[120%] font-bold text-[#183344]">
            There was an issue with your order
          </h1>
          <p className="mb-8 max-w-[600px] text-center text-[16px] leading-[170%] font-normal text-[#3B3B3B]">
            Please contact our support team so we can review your order and get things back on track. We'll make sure
            your lab visit is processed correctly and keep you updated once it's resolved.
          </p>
          <div className="flex w-full max-w-[600px] flex-wrap gap-3">
            <button
              type="button"
              onClick={handleTryAgain}
              disabled={isCancelling}
              className="min-w-[140px] flex-1 rounded-full bg-[#7CF1C7] px-8 py-3.5 text-center text-[17px] leading-[26px] font-bold text-[#183344] transition-all hover:scale-105 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isCancelling ? 'Cancelling...' : 'Try Again'}
            </button>
            <button
              type="button"
              onClick={handleContactSupport}
              disabled={isCancelling}
              className="min-w-[140px] flex-1 rounded-full bg-[#7CF1C7] px-8 py-3.5 text-center text-[17px] leading-[26px] font-bold text-[#183344] transition-all hover:scale-105 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
            >
              Contact Support
            </button>
          </div>
        </div>
      </LabsOrderStepLayout>
    );
  }

  const handleDownloadOrder = () => {
    if (activeOrder?.orderId) {
      downloadOrder(activeOrder.orderId);
    }
  };

  const handleFinish = () => {
    navigate(ROUTE_LABS);
  };

  return (
    <LabsOrderStepLayout currentStep={5} onBack={handleBack} onClose={handleClose}>
      <div className="mx-auto max-w-[600px]">
        {/* Title */}
        <h1 className="mb-4 text-center text-[32px] leading-[120%] font-bold text-[#183344]">Your labs are ordered!</h1>

        {/* Subtitle */}
        <p className="mb-12 text-center text-[16px] leading-[170%] font-normal text-[#3B3B3B]">
          To make sure your lab results are accurate, please follow these simple steps before your appointment:
        </p>

        {/* Checklist */}
        <div className="mb-12 flex justify-center">
          <div className="w-full max-w-[500px] space-y-6">
            <div className="flex items-start gap-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#E8EEF0]">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M13.3337 4L6.00033 11.3333L2.66699 8"
                    stroke="#183344"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <p className="flex-1 pt-1 text-[16px] leading-[140%] text-[#3B3B3B]">
                Avoid strenuous exercise and alcohol 12 hours before
              </p>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#E8EEF0]">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M13.3337 4L6.00033 11.3333L2.66699 8"
                    stroke="#183344"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <p className="flex-1 pt-1 text-[16px] leading-[140%] text-[#3B3B3B]">
                Drink plenty of water on the day of your visit
              </p>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#E8EEF0]">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M13.3337 4L6.00033 11.3333L2.66699 8"
                    stroke="#183344"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <p className="flex-1 pt-1 text-[16px] leading-[140%] text-[#3B3B3B]">
                Your lab will have your order - no need to print anything
              </p>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#E8EEF0]">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M13.3337 4L6.00033 11.3333L2.66699 8"
                    stroke="#183344"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <p className="flex-1 pt-1 text-[16px] leading-[140%] text-[#3B3B3B]">
                You can walk in or schedule directly with the lab
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-4">
          {isPolling ? (
            // Polling state - order is pending signature
            <button
              disabled
              className="flex w-full cursor-not-allowed items-center justify-center gap-3 rounded-full bg-[#183344] px-8 py-3.5 text-center text-[17px] leading-[26px] font-bold text-white opacity-50"
            >
              <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-white"></div>
              Preparing your order form...
            </button>
          ) : hasTimedOut ? (
            // Timeout state - polling exceeded 2 minutes
            <div className="space-y-4">
              <div className="rounded-lg border border-[#FFE69C] bg-[#FFF3CD] p-4">
                <p className="text-center text-[14px] leading-[170%] text-[#664D03]">
                  Your order form is taking longer than usual to prepare. You can safely leave this page and check again
                  later
                </p>
              </div>
              <button
                disabled
                className="w-full cursor-not-allowed rounded-full bg-[#183344] px-8 py-3.5 text-center text-[17px] leading-[26px] font-bold text-white opacity-50"
              >
                Download Your Order
              </button>
            </div>
          ) : (
            // Normal state - order is ready
            <button
              onClick={handleDownloadOrder}
              disabled={isDownloading}
              className="w-full rounded-full bg-[#183344] px-8 py-3.5 text-center text-[17px] leading-[26px] font-bold text-white transition-all hover:bg-[#1a3d4f] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isDownloading ? 'Downloading...' : 'Download Your Order'}
            </button>
          )}

          <button
            onClick={handleFinish}
            className="w-full rounded-full bg-[#7CF1C7] px-8 py-3.5 text-center text-[17px] leading-[26px] font-bold text-[#183344] transition-all hover:scale-105 hover:shadow-lg"
          >
            Finish
          </button>
        </div>
      </div>
    </LabsOrderStepLayout>
  );
}
